import { API_CONFIG } from '../config/api';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get auth token
const getToken = () => localStorage.getItem('access_token');

//   Define proper types for recommendation and status
type FeedbackStatusType = 'submitted' | 'draft';

//   Updated interface with proper types
interface PanelistFeedbackRequest {
  feedback_type: "panelist_interview_feedback";
  provided_by: string;
  job_id: string;
  resume_id: string;
  feedback_text: string; // From "Additional comments"
  rating: number;
  recommendation: string; // From "Interviewer's recommendation"
  status: 'submitted' | 'draft';
  tags: string[]; // From tags section
  
  // Maps for structured data
  competency_assessments: Record<string, any>; // All competency values as key-value pairs
  technical_competencies: Record<string, any>; // Technical competency data as key-value pairs
  
  // Core feedback fields
  overall_fit: number; // From "Overall rating"
  interview_date: string;
  questions_asked: string[]; // From questions asked section
  candidate_responses: string;
  weaknesses: string; // From "Areas to probe"
  final_recommendation: string; // From "Applicable category"
  
  // Additional fields
  is_same_person?: boolean;
  interviewer_name?: string;
  feedback_date?: string;
  
  // Backward compatibility - these fields can be derived from Maps if needed
  skills_match?: number;
  experience_relevance?: number;
  interview_round?: 'screening' | 'technical' | 'behavioral' | 'final';
  communication_skills?: number;
  problem_solving?: number;
  cultural_fit?: number;
  leadership_potential?: number;
  strengths?: string;
  proceed_to_next_round?: boolean;
}

export const feedbackService = {
  // Update method to clarify that first parameter is resumeId, not userId
  async submitPanelistFeedback(resumeId: string, feedbackData: PanelistFeedbackRequest): Promise<any> {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    try {
      console.log('Submitting panelist feedback for resume ID:', resumeId);
      console.log('Panelist feedback data:', feedbackData);
      
      // The API endpoint uses resumeId as the parameter
      const response = await fetch(`${BASE_URL}/feedback/panelist/${resumeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Panelist Feedback API Error:', errorText);
        
        if (response.status === 404) {
          throw new Error(`Resume not found. Please verify the resume ID: ${resumeId}`);
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request data: ${errorText}`);
        } else {
          throw new Error(`Failed to submit panelist feedback: ${response.status} ${response.statusText} - ${errorText}`);
        }
      }

      const result = await response.json();
      console.log('Panelist feedback submitted successfully:', result);
      return result;
    } catch (error) {
      console.error('Error submitting panelist feedback:', error);
      throw error;
    }
  },

  // EXISTING: Keep your existing methods exactly as they were
  async getFeedbackByResumeId(resumeId: string) {
    const token = getToken();
    
    try {
      const response = await fetch(`${BASE_URL}/feedback/resume/${resumeId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch feedback: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  },

  async getFeedbackByPanelist(panelistId: string) {
    const token = getToken();
    
    try {
      const response = await fetch(`${BASE_URL}/feedback/panelist/${panelistId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch panelist feedback: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching panelist feedback:', error);
      throw error;
    }
  },

  // PLACEHOLDER: Add these as stubs so existing components don't break
  async getFeedbackByEntity(entityType: string, entityId: string) {
    console.warn('getFeedbackByEntity not implemented yet');
    return [];
  },

  async submitFeedback(feedbackData: any) {
    console.warn('submitFeedback not implemented yet');
    return { success: false, message: 'Generic feedback submission not implemented' };
  },

  // HR Job Description Feedback
  async submitHRJobFeedback(jobId: string, feedbackData: any): Promise<any> {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    try {
      console.log('Submitting HR JD feedback for job ID:', jobId);
      console.log('HR Feedback data:', feedbackData);
      
      const response = await fetch(`${BASE_URL}/feedback/hr/${jobId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('HR JD Feedback API Error:', errorText);
        
        if (response.status === 404) {
          throw new Error(`Job not found. Please verify the job ID: ${jobId}`);
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request data: ${errorText}`);
        } else {
          throw new Error(`Failed to submit HR feedback: ${response.status} ${response.statusText} - ${errorText}`);
        }
      }

      const result = await response.json();
      console.log('HR JD feedback submitted successfully:', result);
      return result;
    } catch (error) {
      console.error('Error submitting HR JD feedback:', error);
      throw error;
    }
  },

  //  : SME Job Description Feedback (fix method name and endpoint)
  async submitSMEJobFeedback(jobId: string, feedbackData: any): Promise<any> {
    const token = getToken();
    
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    
    try {
      console.log('Submitting SME JD feedback for job ID:', jobId);
      console.log('SME Feedback data:', feedbackData);
      
      const response = await fetch(`${BASE_URL}/feedback/sme/${jobId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('SME JD Feedback API Error:', errorText);
        
        if (response.status === 404) {
          throw new Error(`Job not found. Please verify the job ID: ${jobId}`);
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 400) {
          throw new Error(`Invalid request data: ${errorText}`);
        } else {
          throw new Error(`Failed to submit SME feedback: ${response.status} ${response.statusText} - ${errorText}`);
        }
      }

      const result = await response.json();
      console.log('SME JD feedback submitted successfully:', result);
      return result;
    } catch (error) {
      console.error('Error submitting SME JD feedback:', error);
      throw error;
    }
  },
};